import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import CustomDropdown from '../../Components/Shared/CustomDropdown';
import { getProviderReviews } from '../../api/providers';
import { respondToReview, flagReview } from '../../api/reviews';
import '../AdventureShared.css';
import './ProviderReviewsPage.css';

export default function ProviderReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const flagOptions = [
    { value: 'inappropriate_content', label: 'Inappropriate / Offensive Language' },
    { value: 'spam_or_fake', label: 'Spam or Fake Content' },
    { value: 'harrassment_abuse', label: 'Harassment or Abuse' },
    { value: 'other', label: 'Other Reason' }
  ];
  
  // Stats
  const [stats, setStats] = useState({ avg: 0, total: 0, distribution: [0, 0, 0, 0, 0] });

  // Modals / Input states
  const [replyTextMap, setReplyTextMap] = useState({}); // reviewId -> text
  const [submittingReplyMap, setSubmittingReplyMap] = useState({}); // reviewId -> bool
  const [activeReplyInputId, setActiveReplyInputId] = useState(null); // currently active reply textbox

  // Flag Modal States
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [activeFlagReviewId, setActiveFlagReviewId] = useState(null);
  const [flagReason, setFlagReason] = useState('inappropriate_content');
  const [customFlagReason, setCustomFlagReason] = useState('');
  const [submittingFlag, setSubmittingFlag] = useState(false);

  const currentUserId = localStorage.getItem("userId") || "";

  const fetchReviews = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      if (!currentUserId) {
        setError("User session not found. Please log in.");
        return;
      }
      const data = await getProviderReviews(currentUserId, { limit: 100 });
      setReviews(data || []);
      
      // Calculate Stats
      if (data && data.length > 0) {
        const total = data.length;
        const sum = data.reduce((acc, curr) => acc + curr.rating, 0);
        const avg = sum / total;
        
        const dist = [0, 0, 0, 0, 0];
        data.forEach((r) => {
          if (r.rating >= 1 && r.rating <= 5) {
            dist[r.rating - 1]++;
          }
        });
        
        setStats({ avg, total, distribution: dist });
      } else {
        setStats({ avg: 0, total: 0, distribution: [0, 0, 0, 0, 0] });
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load reviews. Please verify connection.");
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleReplySubmit = async (reviewId) => {
    const response = replyTextMap[reviewId] || "";
    if (!response.trim()) return alert("Reply response text cannot be empty.");

    setSubmittingReplyMap(prev => ({ ...prev, [reviewId]: true }));
    try {
      await respondToReview(reviewId, { response });
      setReplyTextMap(prev => ({ ...prev, [reviewId]: '' }));
      setActiveReplyInputId(null);
      fetchReviews();
    } catch (err) {
      console.error(err);
      alert("Failed to submit reply response.");
    } finally {
      setSubmittingReplyMap(prev => ({ ...prev, [reviewId]: false }));
    }
  };

  const openFlagModal = (reviewId) => {
    setActiveFlagReviewId(reviewId);
    setFlagReason('inappropriate_content');
    setCustomFlagReason('');
    setFlagModalOpen(true);
  };

  const handleFlagSubmit = async (e) => {
    e.preventDefault();
    if (!activeFlagReviewId) return;

    const reasonStr = flagReason === 'other' ? customFlagReason : flagReason;
    if (!reasonStr.trim()) return alert("Please specify a reason for flagging.");

    setSubmittingFlag(true);
    try {
      await flagReview(activeFlagReviewId, { reason: reasonStr });
      setFlagModalOpen(false);
      fetchReviews();
    } catch (err) {
      console.error(err);
      alert("Failed to flag review.");
    } finally {
      setSubmittingFlag(false);
    }
  };

  if (isLoading) {
    return (
      <div className="ProviderReviewsPage">
        <div className="adventurePageContainer">
          <div className="headerContain"><Header /></div>
          <div className="adventurePageContainer2">
            <div className="sidebarContain"><Sidebar /></div>
            <main className="adv-page-main">
              <div className="adv-state-text" style={{ paddingTop: '80px' }}>Loading reviews dashboard...</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ProviderReviewsPage">
      <div className="adventurePageContainer">
        <div className="headerContain"><Header /></div>
        <div className="adventurePageContainer2">
          <div className="sidebarContain"><Sidebar /></div>
          <main className="adv-page-main">
            {/* Header */}
            <div className="adv-detail-page-header">
              <div>
                <h1 className="adv-page-title">Reviews <span className="adv-page-title-accent">Dashboard</span></h1>
                <p className="adv-page-sub">Manage client ratings, reply to comments, and moderate feedback.</p>
              </div>
            </div>

            {error && <div className="reviews-dashboard-error">{error}</div>}

            <div className="reviews-dashboard-grid animate-fade-in">
              {/* Left Column: Stats & Breakdown */}
              <div className="reviews-stats-panel">
                <div className="adv-card">
                  <h3 className="adv-card-title">Overall Performance</h3>
                  <div className="stats-large-score">
                    <span className="stats-score-value">{stats.avg > 0 ? stats.avg.toFixed(1) : '0.0'}</span>
                    <div className="stats-stars-outer">
                      <span className="stats-stars-inner">★ ★ ★ ★ ★</span>
                    </div>
                    <span className="stats-total-reviews">{stats.total} reviews received</span>
                  </div>

                  <div className="stars-distribution-container">
                    {[5, 4, 3, 2, 1].map((stars) => {
                      const count = stats.distribution[stars - 1];
                      const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
                      return (
                        <div key={stars} className="distribution-row">
                          <span className="dist-star-label">{stars} ★</span>
                          <div className="dist-bar-track">
                            <div className="dist-bar-fill" style={{ width: `${pct}%` }}></div>
                          </div>
                          <span className="dist-count-label">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Column: Reviews List */}
              <div className="reviews-list-panel">
                <div className="adv-card">
                  <h3 className="adv-card-title">All Feedbacks</h3>

                  {reviews.length === 0 ? (
                    <div className="reviews-dashboard-empty">
                      No reviews received yet.
                    </div>
                  ) : (
                    <div className="dashboard-reviews-list">
                      {reviews.map((rev) => (
                        <div key={rev.reviewId} className="dashboard-review-card">
                          <div className="dashboard-review-header">
                            <img
                              src={rev.customerAvatarUrl || "/profilePic.jpg"}
                              alt={rev.customerName}
                              className="review-author-avatar"
                            />
                            <div>
                              <h4 className="review-author-name">{rev.customerName}</h4>
                              <span className="review-date">{new Date(rev.createdAt).toLocaleDateString()}</span>
                            </div>
                            <div className="review-rating-stars">
                              {Array.from({ length: rev.rating }).map((_, i) => (
                                <span key={i} className="review-gold-star">★</span>
                              ))}
                            </div>
                          </div>

                          <p className="review-comment-text">"{rev.comment}"</p>

                          {rev.photoUrls?.length > 0 && (
                            <div className="review-photos-row">
                              {rev.photoUrls.map((url, i) => (
                                <img key={i} src={url} alt="Review attachment" className="review-thumbnail-img" />
                              ))}
                            </div>
                          )}

                          {/* Exiting Reply */}
                          {rev.providerResponse && (
                            <div className="review-dashboard-reply-block">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span className="reply-badge">Your Response</span>
                                <button
                                  className="reply-edit-btn"
                                  onClick={() => {
                                    setActiveReplyInputId(rev.reviewId);
                                    setReplyTextMap(prev => ({ ...prev, [rev.reviewId]: rev.providerResponse }));
                                  }}
                                >
                                  ✏ Edit Response
                                </button>
                              </div>
                              <p className="reply-text">"{rev.providerResponse}"</p>
                            </div>
                          )}

                          {/* Reply Input Box */}
                          {activeReplyInputId === rev.reviewId && (
                            <div className="reply-input-box animate-slide-up">
                              <textarea
                                rows="3"
                                className="reply-textarea-input"
                                placeholder="Write a professional response to this client review..."
                                value={replyTextMap[rev.reviewId] || ''}
                                onChange={(e) => setReplyTextMap(prev => ({ ...prev, [rev.reviewId]: e.target.value }))}
                                disabled={submittingReplyMap[rev.reviewId]}
                              />
                              <div className="reply-actions-row">
                                <button
                                  className="reply-cancel-btn"
                                  onClick={() => {
                                    setActiveReplyInputId(null);
                                    setReplyTextMap(prev => ({ ...prev, [rev.reviewId]: '' }));
                                  }}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="reply-submit-btn"
                                  onClick={() => handleReplySubmit(rev.reviewId)}
                                  disabled={submittingReplyMap[rev.reviewId]}
                                >
                                  {submittingReplyMap[rev.reviewId] ? 'Submitting...' : 'Post Reply'}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Footer Actions */}
                          {activeReplyInputId !== rev.reviewId && (
                            <div className="dashboard-review-footer">
                              {!rev.providerResponse && (
                                <button
                                  className="footer-action-btn"
                                  onClick={() => setActiveReplyInputId(rev.reviewId)}
                                >
                                  💬 Reply to Review
                                </button>
                              )}
                              
                              {rev.isFlagged ? (
                                <span className="flagged-indicator">🚩 Flagged for moderation</span>
                              ) : (
                                <button
                                  className="footer-action-btn footer-action-btn--flag"
                                  onClick={() => openFlagModal(rev.reviewId)}
                                >
                                  ⚠️ Flag Review
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Flag Modal */}
            {flagModalOpen && (
              <div className="adv-modal-overlay" style={{ zIndex: 120000 }} onClick={() => setFlagModalOpen(false)}>
                <div className="adv-modal" onClick={(e) => e.stopPropagation()}>
                  <h3 className="adv-modal__title">Flag Review for Moderation</h3>
                  <p className="adv-modal__desc">
                    If this review contains spam, abusive language, or violates platform policies, flag it for administrator review.
                  </p>
                  <form onSubmit={handleFlagSubmit}>
                    <div className="adv-modal__field">
                      <label className="adv-modal__label">Reason for Flagging *</label>
                      <CustomDropdown
                        value={flagReason}
                        onChange={(val) => setFlagReason(val)}
                        options={flagOptions}
                      />
                    </div>

                    {flagReason === 'other' && (
                      <div className="adv-modal__field animate-slide-up">
                        <label className="adv-modal__label">Describe the Reason *</label>
                        <textarea
                          rows="3"
                          required
                          placeholder="Provide details about why this review should be removed..."
                          value={customFlagReason}
                          onChange={(e) => setCustomFlagReason(e.target.value)}
                          className="adv-modal__textarea"
                        />
                      </div>
                    )}

                    <div className="adv-modal__actions">
                      <button
                        type="button"
                        className="adv-btn-ghost"
                        onClick={() => setFlagModalOpen(false)}
                        disabled={submittingFlag}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="adv-btn-destructive"
                        disabled={submittingFlag}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                      >
                        {submittingFlag ? 'Flagging...' : 'Flag Review'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
